import { useState, useCallback, useMemo } from "react";
import {
  usePackingItems,
  usePackingProgress,
  useCreatePackingItem,
  useTogglePackingItem,
  useDeletePackingItem,
  useSetPriority,
} from "@/application/hooks/usePacking";
import { usePackingFilters } from "@/application/hooks/usePackingFilters";
import { usePackingEditMode } from "@/application/hooks/usePackingEditMode";
import { usePackingAiSuggestions } from "@/application/hooks/usePackingAiSuggestions";
import { usePackingTemplates } from "@/application/hooks/usePackingTemplates";
import { useErrorHandler } from "@/application/hooks/useErrorHandler";
import { PackingProvider, type PackingContextValue } from "./PackingContext";
import { PackingList } from "./PackingList";

interface PackingListContainerProps {
  tripId: string;
}

/**
 * PackingListContainer - handles all data fetching and state management.
 * Provides context to all packing sub-components.
 */
export function PackingListContainer({ tripId }: PackingListContainerProps) {
  const { handleError, showSuccess } = useErrorHandler();

  // ==================== Data Fetching Hooks ====================
  const { data: items = [], isLoading } = usePackingItems(tripId);
  const { data: progress } = usePackingProgress(tripId);

  // ==================== Mutation Hooks ====================
  const createMutation = useCreatePackingItem(tripId);
  const toggleMutation = useTogglePackingItem(tripId);
  const deleteMutation = useDeletePackingItem(tripId);
  const priorityMutation = useSetPriority(tripId);

  // ==================== Feature Hooks ====================
  const filters = usePackingFilters();
  const editMode = usePackingEditMode(tripId, items);
  const aiSuggestions = usePackingAiSuggestions(tripId, items);
  const templates = usePackingTemplates(tripId, items);

  // ==================== Form State ====================
  const [newItemName, setNewItemName] = useState("");

  // ==================== Form Handlers ====================
  const handleAddItem = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!newItemName.trim()) return;

      try {
        await createMutation.mutateAsync({
          name: newItemName.trim(),
        });
        setNewItemName("");
        showSuccess("packing.addSuccess", "持ち物を追加しました");
      } catch (error) {
        handleError(error, { message: "持ち物の追加に失敗しました" });
      }
    },
    [newItemName, createMutation, showSuccess, handleError]
  );

  // ==================== Item Handlers ====================
  const handleToggle = useCallback(
    async (itemId: string) => {
      try {
        await toggleMutation.mutateAsync(itemId);
      } catch (error) {
        handleError(error, { message: "チェックの切り替えに失敗しました" });
      }
    },
    [toggleMutation, handleError]
  );

  const handleDelete = useCallback(
    async (itemId: string) => {
      try {
        await deleteMutation.mutateAsync(itemId);
        showSuccess("packing.deleteSuccess", "持ち物を削除しました");
      } catch (error) {
        handleError(error, { message: "持ち物の削除に失敗しました" });
      }
    },
    [deleteMutation, showSuccess, handleError]
  );

  const handleSetPriority = useCallback(
    async (itemId: string, priority: number | null) => {
      try {
        await priorityMutation.mutateAsync({ itemId, priority });
      } catch (error) {
        handleError(error, { message: "優先度の設定に失敗しました" });
      }
    },
    [priorityMutation, handleError]
  );

  // ==================== Build Context Value ====================
  const contextValue: Omit<PackingContextValue, "priorityCounts"> = useMemo(
    () => ({
      // Data
      items,
      progress: progress ?? null,
      isLoading,

      // Form State
      newItemName,
      onNewItemNameChange: setNewItemName,
      onAddItem: handleAddItem,
      isCreating: createMutation.isPending,

      // Filter State
      showUncheckedOnly: filters.showUncheckedOnly,
      priorityFilter: filters.priorityFilter,
      onToggleShowUncheckedOnly: filters.onToggleShowUncheckedOnly,
      onPriorityFilterChange: filters.onPriorityFilterChange,

      // Edit Mode State
      isEditMode: editMode.isEditMode,
      selectedItemIds: editMode.selectedItemIds,
      onToggleEditMode: editMode.onToggleEditMode,
      onToggleItemSelection: editMode.onToggleItemSelection,
      onSelectAllItems: editMode.onSelectAllItems,
      onDeselectAllItems: editMode.onDeselectAllItems,
      onBulkDelete: editMode.onBulkDelete,
      onBulkToggleCheck: editMode.onBulkToggleCheck,

      // Item Actions
      onToggleItem: handleToggle,
      onDeleteItem: handleDelete,
      onSetPriority: handleSetPriority,
      isToggling: toggleMutation.isPending || editMode.isToggling,
      isDeleting: deleteMutation.isPending || editMode.isDeleting,
      isSettingPriority: priorityMutation.isPending,

      // AI Suggestions
      aiSuggestions: aiSuggestions.aiSuggestions,
      selectedSuggestions: aiSuggestions.selectedSuggestions,
      showAiPanel: aiSuggestions.showAiPanel,
      isAiLoading: aiSuggestions.isAiLoading,
      isBulkCreating: aiSuggestions.isBulkCreating || templates.isBulkCreating,
      onAiSuggest: aiSuggestions.onAiSuggest,
      onToggleSuggestion: aiSuggestions.onToggleSuggestion,
      onSelectAllSuggestions: aiSuggestions.onSelectAllSuggestions,
      onDeselectAllSuggestions: aiSuggestions.onDeselectAllSuggestions,
      onAddSelectedSuggestions: aiSuggestions.onAddSelectedSuggestions,
      onCloseAiPanel: aiSuggestions.onCloseAiPanel,

      // Templates
      templateItems: templates.templateItems,
      selectedTemplateItems: templates.selectedTemplateItems,
      showTemplatePanel: templates.showTemplatePanel,
      selectedTripType: templates.selectedTripType,
      onSelectTemplate: templates.onSelectTemplate,
      onToggleTemplateItem: templates.onToggleTemplateItem,
      onSelectAllTemplateItems: templates.onSelectAllTemplateItems,
      onDeselectAllTemplateItems: templates.onDeselectAllTemplateItems,
      onAddSelectedTemplateItems: templates.onAddSelectedTemplateItems,
      onCloseTemplatePanel: templates.onCloseTemplatePanel,
    }),
    [
      items,
      progress,
      isLoading,
      newItemName,
      handleAddItem,
      createMutation.isPending,
      filters,
      editMode,
      handleToggle,
      handleDelete,
      handleSetPriority,
      toggleMutation.isPending,
      deleteMutation.isPending,
      priorityMutation.isPending,
      aiSuggestions,
      templates,
    ]
  );

  // ==================== Render ====================
  return (
    <PackingProvider value={contextValue}>
      <PackingList />
    </PackingProvider>
  );
}
